<template>
  <div class="content-main">
    <div class="content">
      <div class="details-header">
        <button class="btn-back" @click="goBack">
          ← {{ $t("message.forms_page.back") }}
        </button>
        <h1>
          {{
            $t("message.forms_page.submission_details") || "Submission Details"
          }}
        </h1>
      </div>

      <div v-if="loading" class="loading-state">
        <BaseMiniSpinner />
      </div>

      <div v-else-if="submission && form" class="details-container">
        <div class="submission-header-card">
          <div class="header-info">
            <div class="info-group">
              <label>{{ $t("message.forms_page.form_name") }}</label>
              <p>{{ form.name }}</p>
            </div>
            <div class="info-group">
              <label>{{ $t("message.forms_page.date") || "Date" }}</label>
              <p>{{ formatDate(submission.created_at) }}</p>
            </div>
            <div class="info-group">
              <label>ID</label>
              <p>{{ submission.id }}</p>
            </div>
            <div class="info-group">
              <label>{{ $t("message.forms_page.status") || "Status" }}</label>
              <p>
                <span
                  :class="[
                    'status-badge',
                    submission.is_processed == 1 ? 'processed' : 'new'
                  ]"
                >
                  {{ submission.is_processed == 1 ? "Processed" : "New" }}
                </span>
              </p>
            </div>
            <div v-if="submission.seller_name" class="info-group">
              <label>{{ $t("message.forms_page.seller") || "Seller" }}</label>
              <p>{{ submission.seller_name }}</p>
            </div>
          </div>
          <div class="header-actions">
            <button
              v-if="submission.is_processed != 1"
              class="btn-action btn-mark-processed"
              @click="markAsProcessed"
              :disabled="processingSubmission"
            >
              {{
                processingSubmission
                  ? "..."
                  : $t("message.forms_page.mark_processed") || "Mark Processed"
              }}
            </button>
            <button
              class="btn-action btn-add-customer"
              @click="openAddCustomerWithAI"
              :disabled="aiMapping"
            >
              <span v-if="!aiMapping">{{
                $t("message.forms_page.add_customer")
              }}</span>
              <span v-else>✦ Mapping...</span>
            </button>
          </div>
        </div>

        <div class="fields-container">
          <div class="fields-header">
            <h2>{{ $t("message.forms_page.responses") || "Responses" }}</h2>
            <button class="btn-copy-all" @click="copyAllResponses">
              <span class="copy-icon">⎘</span>
              {{ copiedAll ? "Copied!" : "Copy All" }}
            </button>
          </div>
          <div class="fields-grid">
            <div
              v-for="field in form.definition.fields"
              :key="field.id"
              class="field-card"
            >
              <div class="field-header">
                <label class="field-label">{{ field.label }}</label>
                <div class="field-header-right">
                  <span v-if="field.required" class="required">*</span>
                  <button
                    class="btn-copy-field"
                    :class="{ copied: copiedFieldId === field.id }"
                    @click="copyField(field)"
                    title="Copy value"
                  >
                    {{ copiedFieldId === field.id ? "✓" : "⎘" }}
                  </button>
                </div>
              </div>
              <div class="field-value">
                {{ getFieldValue(submission.responses, field.id) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="error-state">
        {{ $t("message.forms_page.not_found") || "Submission not found" }}
      </div>

      <!-- Customer Modal -->
      <InfoautoCreateCustomerModal
        v-if="showCreateCustomerModal"
        @close="closeCustomerModal"
        @newCustomer="onNewCustomer"
        ref="customerModal"
      />
    </div>
  </div>
</template>

<script>
import { getSubmission, mapCustomerWithAI } from "@/api/store/FormsService.js";
import InfoautoCreateCustomerModal from "@/components/customer/InfoautoCreateCustomerModal.vue";
import BaseMiniSpinner from "@/components/global/_base-mini-spinner.vue";

export default {
  name: "FormSubmissionDetailsPage",
  components: {
    InfoautoCreateCustomerModal,
    BaseMiniSpinner
  },
  data() {
    return {
      submission: null,
      form: null,
      loading: true,
      processingSubmission: false,
      showCreateCustomerModal: false,
      aiMapping: false,
      copiedAll: false,
      copiedFieldId: null
    };
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const submissionId = parseInt(this.$route.params.submissionId);

        const submission = await getSubmission(submissionId);
        if (!submission) {
          throw new Error("Submission not found");
        }

        this.submission = submission;
        this.form = {
          name: submission.form_name,
          definition: submission.form_definition
        };
        if (this.form.definition && this.form.definition.fields) {
          this.form.definition.fields.forEach(field => {
            if (!field.options) {
              field.options = [];
            }
          });
        }
      } catch (e) {
        this.$toast.open({
          message: e.message || "Error loading submission details",
          type: "error",
          position: "top"
        });
      } finally {
        this.loading = false;
      }
    },

    async markAsProcessed() {
      try {
        this.processingSubmission = true;
        const { markSubmissionAsProcessed } = await import(
          "@/api/store/FormsService.js"
        );
        await markSubmissionAsProcessed(this.submission.id);
        this.submission.is_processed = 1;
        this.$toast.open({
          message: "Submission marked as processed",
          type: "success",
          position: "top"
        });
      } catch (e) {
        this.$toast.open({
          message: e.message || "Error marking submission as processed",
          type: "error",
          position: "top"
        });
      } finally {
        this.processingSubmission = false;
      }
    },

    async openAddCustomerWithAI() {
      this.aiMapping = true;
      try {
        const fields = this.form.definition.fields.map(field => ({
          label: field.label,
          value: this.getFieldValue(this.submission.responses, field.id)
        }));

        const mapped = await mapCustomerWithAI(fields);

        this.showCreateCustomerModal = true;
        this.$nextTick(() => {
          if (!this.$refs.customerModal) return;
          const c = this.$refs.customerModal;
          if (mapped.type) c.newCustomer.type = mapped.type;
          if (mapped.name) c.newCustomer.name = mapped.name;
          if (mapped.surname) c.newCustomer.surname = mapped.surname;
          if (mapped.companyName)
            c.newCustomer.companyName = mapped.companyName;
          if (mapped.email) c.newCustomer.email = mapped.email;
          if (mapped.phone) c.newCustomer.phone = mapped.phone;
          if (mapped.ri) c.newCustomer.ri = mapped.ri;
        });
      } catch (e) {
        this.$toast.open({
          message:
            e.response?.data?.error ||
            e.message ||
            "Error mapping customer data",
          type: "error",
          position: "top"
        });
      } finally {
        this.aiMapping = false;
      }
    },

    copyField(field) {
      const value = this.getFieldValue(this.submission.responses, field.id);
      navigator.clipboard.writeText(value).then(() => {
        this.copiedFieldId = field.id;
        setTimeout(() => {
          this.copiedFieldId = null;
        }, 1500);
      });
    },

    copyAllResponses() {
      const lines = this.form.definition.fields.map(field => {
        const value = this.getFieldValue(this.submission.responses, field.id);
        return `${field.label}: ${value}`;
      });
      navigator.clipboard.writeText(lines.join("\n")).then(() => {
        this.copiedAll = true;
        setTimeout(() => {
          this.copiedAll = false;
        }, 1500);
      });
    },

    closeCustomerModal() {
      this.showCreateCustomerModal = false;
    },

    onNewCustomer() {
      this.closeCustomerModal();
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        })
      );
    },

    getFieldValue(responses, fieldId) {
      // camelize (npm) converts ALL _\w: field_1→field1, field_name→fieldName
      const camelKey = fieldId.replace(/[_-](\w)/g, (_, c) => c.toUpperCase());
      const value =
        responses[fieldId] !== undefined
          ? responses[fieldId]
          : responses[camelKey];
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      if (typeof value === "object" && value !== null) {
        return JSON.stringify(value, null, 2);
      }
      return value !== undefined && value !== null && value !== ""
        ? value
        : "-";
    },

    goBack() {
      this.$router.back();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.content-main {
  padding: 20px;
}

.content {
  max-width: 1200px;
  margin: 0 auto;
}

.details-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;

  .btn-back {
    padding: 8px 16px;
    background: #e0e0e0;
    color: #333;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
      background: #d0d0d0;
    }
  }

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 600;
    color: #333;
  }
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 60px 20px;
}

.error-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
}

.details-container {
  display: grid;
  gap: 20px;
}

.submission-header-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;

  .header-info {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    flex: 1;

    .info-group {
      label {
        display: block;
        font-weight: 600;
        color: #666;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
      }

      p {
        margin: 0;
        font-size: 16px;
        color: #333;
        font-weight: 500;

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 600;

          &.new {
            background: #c8e6c9;
            color: #1b5e20;
          }

          &.processed {
            background: #e0e0e0;
            color: #666;
          }
        }
      }
    }
  }

  .header-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex-shrink: 0;

    .btn-action {
      padding: 10px 20px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s ease;
      white-space: nowrap;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      &.btn-mark-processed {
        background: #2196f3;
        color: white;

        &:hover:not(:disabled) {
          background: #1976d2;
        }
      }

      &.btn-add-customer {
        background: #4caf50;
        color: white;

        &:hover {
          background: #45a049;
        }
      }
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .header-info {
      grid-template-columns: 1fr;
    }

    .header-actions {
      width: 100%;
      flex-direction: row;

      .btn-action {
        flex: 1;
      }
    }
  }
}

.fields-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;

  h2 {
    margin: 0 0 20px 0;
    font-size: 20px;
    font-weight: 600;
    color: #333;
  }

  .fields-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;

    .field-card {
      padding: 16px;
      background: #f9f9f9;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      border-left: 4px solid #2196f3;

      .field-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;

        .field-label {
          font-weight: 600;
          color: #333;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0;
        }

        .required {
          color: #e74c3c;
          font-weight: bold;
        }
      }

      .field-value {
        font-size: 14px;
        color: #555;
        line-height: 1.6;
        white-space: pre-wrap;
        word-break: break-word;
        font-family: "Menlo", "Monaco", monospace;
      }
    }
  }

  @media (max-width: 768px) {
    .fields-grid {
      grid-template-columns: 1fr;
    }
  }
}

.fields-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #333;
  }
}

.btn-copy-all {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e0e0e0;
  }
}

.copy-icon {
  font-size: 15px;
}

.field-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-copy-field {
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  color: #aaa;
  font-size: 14px;
  cursor: pointer;
  padding: 2px 6px;
  transition: all 0.15s;
  line-height: 1;

  &:hover {
    background: #eee;
    color: #555;
    border-color: #ddd;
  }
  &.copied {
    color: #4caf50;
  }
}

.btn-add-customer {
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
